import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import {
  Search,
  Sparkles,
  User,
  Settings,
  ChevronDown,
  ChevronsLeft,
  ChevronsRight,
  Sun,
  Moon,
  LogOut,
  CommandIcon,
  ListChecks,
  CreditCard,
  ShieldCheck,
} from "lucide-react";
import { useTheme } from "next-themes";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";
import { planLabel } from "@/lib/plans";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const nav = [
  { to: "/research", label: "Research", icon: Search, tid: "nav-research-link" },
  { to: "/enrich", label: "Enrich", icon: Sparkles, tid: "nav-enrich-link" },
  { to: "/lists", label: "Lists", icon: ListChecks, tid: "nav-lists-link" },
  { to: "/profile", label: "Profile", icon: User, tid: "nav-profile-link" },
  { to: "/billing", label: "Billing", icon: CreditCard, tid: "nav-billing-link" },
];
const adminNav = { to: "/admin", label: "Admin", icon: ShieldCheck, tid: "nav-admin-link" };

export default function Sidebar({ onOpenCommand, collapsed, onToggleCollapsed }) {
  const { user, logout } = useAuth();
  // `theme` is the choice (light / dark / system); `resolvedTheme` is what is
  // on screen, so the quick toggle flips what the user actually sees.
  const { theme, resolvedTheme, setTheme } = useTheme();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const initials = (user?.name || user?.email || "U")
    .split(/\s+|@/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join("");

  const creditsTotal = user?.credits_total ?? 0;
  const creditsUsed = user?.credits_used ?? 0;
  const creditsRemaining = creditsTotal - creditsUsed;
  const creditsPct = creditsTotal > 0 ? Math.max(0, (creditsRemaining / creditsTotal) * 100) : 0;
  // Under 20% the meter turns amber and offers a top-up (design system: Credits).
  const creditsLow = creditsPct < 20;
  const items = user?.role === "admin" ? [...nav, adminNav] : nav;

  return (
    <aside
      data-testid="app-sidebar"
      data-collapsed={collapsed ? "true" : "false"}
      className={`hidden md:flex shrink-0 flex-col justify-between border-r border-border bg-surface transition-[width] duration-200 ease-out ${
        collapsed ? "w-14" : "w-60"
      }`}
    >
      <TooltipProvider delayDuration={200}>
        <div className="flex flex-col gap-3 p-2">
          {/* Logo lockup — the mark alone when collapsed */}
          <div className={collapsed ? "mx-auto pt-2" : "px-2 pt-2"}>
            <Logo size={collapsed ? 24 : 26} showText={!collapsed} />
          </div>

          {/* Workspace switcher */}
          {collapsed ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => navigate("/profile")}
                  data-testid="workspace-switcher-collapsed"
                  className="grid h-10 w-10 mx-auto place-items-center rounded-md bg-primary text-primary-foreground font-display text-sm font-semibold"
                >
                  {initials || "IL"}
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">{user?.company?.trim() || "InboundLabs"}</TooltipContent>
            </Tooltip>
          ) : (
            <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="workspace-switcher"
                  className="flex w-full items-center justify-between gap-2 rounded-md px-2 py-2 text-left hover:bg-surface-hover"
                >
                  <div className="flex min-w-0 items-center gap-2">
                    <div className="grid h-7 w-7 shrink-0 place-items-center rounded-md bg-primary text-primary-foreground font-display text-sm font-semibold">
                      {initials || "IL"}
                    </div>
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium leading-tight">
                        {user?.company?.trim() || "InboundLabs"}
                      </div>
                      <div className="truncate text-xs text-muted-foreground leading-tight">
                        {user?.email}
                      </div>
                    </div>
                  </div>
                  <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                <DropdownMenuLabel>Workspace</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="workspace-settings-item">
                  <Settings className="h-4 w-4" /> Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel>Theme</DropdownMenuLabel>
                <DropdownMenuRadioGroup value={theme || "system"} onValueChange={setTheme}>
                  <DropdownMenuRadioItem value="light" data-testid="theme-option-light">Light</DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value="dark" data-testid="theme-option-dark">Dark</DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value="system" data-testid="theme-option-system">Match my computer</DropdownMenuRadioItem>
                </DropdownMenuRadioGroup>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="logout-menu-item">
                  <LogOut className="h-4 w-4" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* Cmd+K search */}
          {collapsed ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={onOpenCommand}
                  data-testid="open-command-palette-collapsed"
                  className="mx-auto grid h-9 w-9 place-items-center rounded-md border border-border bg-background/40 text-muted-foreground hover:bg-surface-hover"
                  aria-label="Search"
                >
                  <Search className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">Search (⌘K)</TooltipContent>
            </Tooltip>
          ) : (
            <button
              data-testid="open-command-palette"
              onClick={onOpenCommand}
              className="flex w-full items-center justify-between rounded-md border border-border bg-background/40 px-3 py-1.5 text-sm text-muted-foreground hover:bg-surface-hover"
            >
              <span className="flex items-center gap-2">
                <Search className="h-3.5 w-3.5" />
                Search…
              </span>
              <kbd className="font-mono-bd flex items-center gap-0.5 rounded border border-border bg-surface px-1 py-0.5 text-[10px]">
                <CommandIcon className="h-3 w-3" />K
              </kbd>
            </button>
          )}

          {/* Nav */}
          <nav className="mt-1 flex flex-col gap-0.5">
            {!collapsed && (
              <div className="px-2 pb-1 eyebrow">
                Workspace
              </div>
            )}
            {items.map(({ to, label, icon: Icon, tid }) => {
              const link = (
                <NavLink
                  key={to}
                  to={to}
                  data-testid={tid}
                  className={({ isActive }) =>
                    `flex items-center gap-2 rounded-md py-1.5 text-sm transition-colors ${
                      collapsed ? "mx-auto justify-center w-9 h-9" : "px-2"
                    } ${
                      isActive
                        ? "bg-violet-soft font-medium text-violet-text"
                        : "text-ink-secondary hover:bg-surface-hover hover:text-foreground"
                    }`
                  }
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  {!collapsed && <span>{label}</span>}
                </NavLink>
              );
              return collapsed ? (
                <Tooltip key={to}>
                  <TooltipTrigger asChild>{link}</TooltipTrigger>
                  <TooltipContent side="right">{label}</TooltipContent>
                </Tooltip>
              ) : link;
            })}
          </nav>
        </div>
      </TooltipProvider>

      {/* Credits widget + footer */}
      <div className="border-t border-border">
        {!collapsed && (
          <div className="mx-2 mt-2 mb-1 rounded-lg border border-border bg-surface-inset p-3" data-testid="credits-widget">
            <div className="mb-2 flex items-center justify-between gap-2">
              <span className="text-xs text-ink-secondary">Credits remaining</span>
              <span className="rounded-full bg-violet-soft px-2 py-0.5 text-[10px] font-semibold text-violet-text" data-testid="sidebar-plan">
                {planLabel(user?.plan)}
              </span>
            </div>
            <div className="h-1.5 bg-border rounded-full overflow-hidden mb-1.5">
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${creditsPct}%`,
                  background: creditsLow
                    ? "hsl(var(--warning))"
                    : "linear-gradient(90deg, hsl(var(--primary)), hsl(var(--orchid)))",
                }}
              />
            </div>
            <div className="flex items-center justify-between gap-2">
              <div className={`text-xs tabular-nums ${creditsLow ? "text-warning" : "text-muted-foreground"}`}>
                {creditsRemaining} / {creditsTotal}
              </div>
              <Link to="/choose-plan" className="text-xs font-semibold text-violet-text hover:underline" data-testid="sidebar-upgrade">
                {creditsLow ? "Top up" : user?.plan && user.plan !== "free" ? "Plans" : "Upgrade"}
              </Link>
            </div>
          </div>
        )}

        <div className={`flex items-center ${collapsed ? "flex-col gap-2 py-3" : "justify-end p-3"}`}>
          <div className={`flex ${collapsed ? "flex-col gap-1" : "items-center gap-1"}`}>
            <Button
              data-testid="theme-toggle"
              variant="ghost"
              size="icon"
              onClick={() => setTheme(resolvedTheme === "dark" ? "light" : "dark")}
              aria-label="Toggle theme"
            >
              {resolvedTheme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
            <Button
              data-testid="toggle-sidebar"
              variant="ghost"
              size="icon"
              onClick={onToggleCollapsed}
              aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {collapsed ? <ChevronsRight className="h-4 w-4" /> : <ChevronsLeft className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </div>
    </aside>
  );
}
